import { isNumber } from 'lodash';
import { nullifyEmptyValue } from 'libs/utils/object';
import { SearchSuggestionType } from 'constants/search';
import { extraServiceOrderTypeToTrackingTypeMap } from 'constants/extra-service';

const clickEvent = args => {
  const {
    target,
    screen,
    targetDetails,
    env,
    path
  } = args;
  const extra = {
    target
  };
  if (screen) extra.screen = screen;
  if (targetDetails) extra.target_details = targetDetails;
  if (env) extra.env = env;
  if (path) extra.path = path;
  return {
    event: 'user.click',
    extra
  };
};

const kycClickEvent = args => {
  const {
    target,
    details
  } = args;
  const extra = {
    target
  };
  if (details) extra.details = details;
  return {
    event: 'kyc.user.click',
    extra
  };
};

const favouriteItemEvent = _ref => {
  let {
    itemId,
    isFollowEvent,
    contentSource,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    homepageSessionId
  } = _ref;
  const eventName = isFollowEvent ? 'user.favorite_item' : 'user.unfavorite_item';
  const extra = {
    item_id: itemId
  };
  if (contentSource) extra.content_source = contentSource;
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId;
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId;
  return {
    event: eventName,
    extra
  };
};

const followUserEvent = _ref2 => {
  let {
    userId,
    isFollowEvent,
    contentSource
  } = _ref2;
  const eventName = isFollowEvent ? 'user.follow_user' : 'user.unfollow_user';
  const extra = {
    user_id: userId
  };
  if (isFollowEvent) extra.content_source = contentSource;
  return {
    event: eventName,
    extra
  };
};

const followBrandEvent = _ref3 => {
  let {
    brandId,
    isFollowEvent
  } = _ref3;
  const eventName = isFollowEvent ? 'user.follow_brand' : 'user.unfollow_brand';
  const extra = {
    brand_id: brandId
  };
  return {
    event: eventName,
    extra
  };
};

const impressionEvent = args => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    homepageSessionId
  } = args;
  const extra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource
  };
  if (itemOwnerId) extra.item_owner_id = itemOwnerId;
  if (searchScore) extra.search_score = JSON.stringify(searchScore);
  if (searchSignals) extra.search_signals = searchSignals;
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId;
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance;
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId;
  return {
    event: 'list.show_item',
    extra
  };
};

const clickListItemEvent = args => {
  const {
    id,
    contentType,
    contentSource,
    position,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    searchScore,
    searchSignals,
    itemDistance,
    homepageSessionId
  } = args;
  const extra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource
  };
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId;
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  if (searchScore) extra.search_score = searchScore;
  if (searchSignals) extra.search_signals = searchSignals;
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance;
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId;
  return {
    event: 'user.click_list_item',
    extra
  };
};

const clickClosetPromotionEvent = args => {
  const {
    contentSource,
    target,
    ownerId
  } = args;
  const extra = {
    owner_id: ownerId,
    content_source: contentSource,
    target
  };
  return {
    event: 'user.click_closet_promotion',
    extra
  };
};

const endOfListEvent = args => {
  const {
    itemCount,
    page,
    perPage
  } = args;
  const extra = {
    item_count: itemCount
  };
  if (page) extra.page = page;
  if (perPage) extra.per_page = perPage;
  return {
    event: 'user.reach_end_of_list',
    extra
  };
};

const viewEvent = args => {
  const {
    target,
    targetDetails,
    screen
  } = args;
  const extra = {
    target
  };
  if (targetDetails) extra.target_details = targetDetails;
  if (screen) extra.screen = screen;
  return {
    event: 'user.view',
    extra
  };
};

const viewScreenEvent = args => {
  const {
    screen
  } = args;
  const extra = {
    screen
  };
  return {
    event: 'user.view_screen',
    extra
  };
};

const viewUserEvent = args => {
  const {
    path,
    user_id
  } = args;
  const extra = {
    path,
    user_id
  };
  return {
    event: 'user.view_user',
    extra
  };
};

const kycViewScreenEvent = args => {
  const {
    screen,
    details
  } = args;
  const extra = {
    screen
  };
  if (details) extra.details = details;
  return {
    event: 'kyc.user.view_screen',
    extra
  };
};

const systemTraceClosetPromoEvent = args => {
  const {
    uuid,
    closetCount,
    itemCount,
    type
  } = args;
  return {
    event: 'system.trace_closet_promo',
    extra: {
      uuid,
      type,
      closet_count: closetCount,
      item_count: itemCount
    }
  };
};

const conversationReplyEvent = args => {
  const {
    itemId,
    userId
  } = args;
  const extra = {
    user_id: userId,
    item_id: itemId
  };
  return {
    event: 'user.reply',
    extra
  };
};

const selectSearchSuggestionEvent = args => {
  const {
    suggestions,
    index,
    suggestion,
    subtitle,
    scopedSuggestionCount,
    query,
    countryCode,
    searchSessionId,
    type,
    typeId,
    globalSearchSessionId,
    suggestionsSessionId,
    params,
    isFrontendGeneratedSuggestion,
    suggestionsListId,
    suggestionPosition
  } = args;
  const extra = {
    suggestion_index: index,
    suggestions_list: suggestions,
    selected_suggestion_text: suggestion,
    filter_suggestions_count: scopedSuggestionCount,
    query,
    global_search_session_id: globalSearchSessionId,
    frontend_generated_text_selected: isFrontendGeneratedSuggestion
  };

  if (subtitle) {
    extra.selected_suggestion_subtitle = subtitle;
  }

  if (countryCode) {
    extra.country_code = countryCode;
  }

  if (searchSessionId) {
    extra.search_session_id = searchSessionId;
  }

  if (params !== null && params !== void 0 && params.length) {
    extra.params = params;
  }

  const allowedSuggestionTypes = [SearchSuggestionType.Catalog, SearchSuggestionType.Brand, SearchSuggestionType.Fallback];

  if (type && allowedSuggestionTypes.includes(type)) {
    extra.type = type;
  }

  if (typeId) {
    extra.type_id = typeId;
  }

  if (suggestionsSessionId) {
    extra.suggestions_session_id = suggestionsSessionId;
  }

  if (suggestionsListId) {
    extra.suggestions_list_id = suggestionsListId;
  }

  if (suggestionPosition) {
    extra.suggestion_position = suggestionPosition;
  }

  return {
    event: 'user.select_search_suggestion',
    extra
  };
};

const viewSearchSuggestionsEvent = args => {
  const {
    suggestions,
    query,
    suggestionsSessionId
  } = args;
  const extra = {
    suggestions_list: suggestions,
    query
  };

  if (suggestionsSessionId) {
    extra.suggestions_session_id = suggestionsSessionId;
  }

  return {
    event: 'user.view_search_suggestions',
    extra
  };
};

const viewSearchSuggestionEvent = args => {
  const {
    suggestionPosition,
    query,
    suggestionsSessionId,
    selectedSuggestionText,
    suggestionsListId,
    params,
    frontendGeneratedTextSelected,
    searchSessionId,
    globalSearchSessionId
  } = args;
  const extra = {
    suggestion_position: suggestionPosition,
    selected_suggestion_text: selectedSuggestionText,
    query,
    suggestions_session_id: suggestionsSessionId,
    suggestions_list_id: suggestionsListId,
    frontend_generated_text_selected: frontendGeneratedTextSelected,
    search_session_id: searchSessionId,
    global_search_session_id: globalSearchSessionId
  };

  if (params !== null && params !== void 0 && params.length) {
    extra.params = params;
  }

  return {
    event: 'user.view_search_suggestion',
    extra
  };
};

const viewShippingPriceEvent = args => {
  const {
    prices,
    screen,
    itemId,
    transactionId
  } = args;
  const extra = {
    prices: prices.length ? prices : null,
    screen
  };
  if (itemId) extra.item_id = itemId;
  if (transactionId) extra.transaction_id = transactionId;
  return {
    event: 'user.view_shipping_price',
    extra
  };
};

const clickFilterEvent = args => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    type,
    code
  } = args;
  const extra = {
    filter,
    screen
  };
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  if (type) extra.type = type;
  if (code) extra.code = code;
  return {
    event: 'user.click_filter',
    extra
  };
};

const viewFilterEvent = args => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    type,
    code
  } = args;
  const extra = {
    filter,
    screen
  };
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  if (type) extra.type = type;
  if (code) extra.code = code;
  return {
    event: 'user.view_filter',
    extra
  };
};

const startConversationEvent = args => {
  const {
    itemId,
    userId
  } = args;
  const extra = {
    user_id: userId,
    item_id: itemId
  };
  return {
    event: 'user.start_conversation',
    extra
  };
};

const checkoutBuyerInputEvent = args => {
  const {
    transactionId,
    targetDetails,
    screen,
    target,
    valid
  } = args;
  const extra = {
    screen,
    target,
    valid
  };
  if (transactionId) extra.transaction_id = transactionId;
  if (targetDetails) extra.target_details = targetDetails;
  return {
    event: 'checkout.buyer.input',
    extra
  };
};

const transactionSellerInputEvent = args => {
  const {
    transactionId,
    targetDetails,
    screen,
    target,
    valid
  } = args;
  const extra = {
    screen,
    target,
    valid
  };
  if (transactionId) extra.transaction_id = transactionId;
  if (targetDetails) extra.target_details = targetDetails;
  return {
    event: 'transaction.seller_input',
    extra
  };
};

const userInputEvent = args => {
  const {
    state,
    target,
    value,
    screen,
    tempUuid,
    isDynamic
  } = args;
  const extra = {
    screen,
    target,
    value,
    state,
    upload_session_id: tempUuid,
    is_dynamic: isDynamic || false
  };
  return {
    event: 'user.input',
    extra
  };
};

const checkoutPickPaymentMethodEvent = args => {
  const {
    transactionId,
    eventTrackingCode,
    screen
  } = args;
  const extra = {
    transaction_id: transactionId,
    payment_method: eventTrackingCode || '',
    screen
  };
  return {
    event: 'buyer.pick_payment_method',
    extra
  };
};

const buyerViewCheckoutEvent = args => {
  const event = 'buyer.view_checkout';

  if ('transactionId' in args) {
    var _args$transactionId;

    const extra = {
      screen: args.screen,
      checkout_type: 'escrow',
      transaction_id: (_args$transactionId = args.transactionId) !== null && _args$transactionId !== void 0 ? _args$transactionId : ''
    };
    return {
      event,
      extra
    };
  }

  const extra = {
    screen: args.screen,
    order_id: args.orderId,
    checkout_type: extraServiceOrderTypeToTrackingTypeMap[args.orderType]
  };
  return {
    event,
    extra
  };
};

const buyerViewSettingsCheckoutEvent = args => {
  return {
    event: 'buyer.view_checkout',
    extra: {
      screen: args.screen,
      checkout_type: 'settings'
    }
  };
};

const userViewCheckoutEvent = args => {
  const {
    screen
  } = args;
  const extra = {
    screen
  };
  return {
    event: 'user.view_checkout',
    extra
  };
};

const selectCatalogEvent = args => {
  const {
    catalogIds,
    attributeId,
    screen
  } = args;
  const extra = {
    catalog_id: catalogIds.join(','),
    attribute_id: attributeId !== null && attributeId !== void 0 ? attributeId : null
  };
  if (screen) extra.screen = screen;
  return {
    event: 'user.select_catalog',
    extra
  };
};

const selfServiceViewScreenEvent = args => {
  const {
    screen,
    transactionId,
    faqEntryId,
    variant
  } = args;
  const extra = {
    screen,
    transaction_id: transactionId
  };
  if (faqEntryId) extra.faq_entry_id = faqEntryId;
  if (variant) extra.variant = variant;
  return {
    event: 'user.self_service.view_screen',
    extra
  };
};

const viewPopularSearchEvent = args => {
  const {
    index,
    query
  } = args;
  const extra = {
    index,
    search_query: query
  };
  return {
    event: 'user.view_popular_search',
    extra
  };
};

const selectPopularSearchEvent = args => {
  const {
    index,
    query
  } = args;
  const extra = {
    index,
    search_query: query
  };
  return {
    event: 'user.select_popular_search',
    extra
  };
};

const selectBrandEvent = args => {
  const {
    brandId,
    brandClusterId,
    position
  } = args;
  const extra = {
    brand_id: brandId,
    brand_cluster_id: brandClusterId,
    position
  };
  return {
    event: 'user.select_brand',
    extra
  };
};

const userFilterItemsEvent = args => {
  const {
    itemCount,
    filters,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    searchStartId,
    searchStartType,
    selectedDynamicFilters
  } = args;
  const extra = {
    item_count: itemCount,
    search_correlation_id: searchCorrelationId,
    search_session_id: searchSessionId,
    global_search_session_id: globalSearchSessionId,
    conditions: {
      search_term: nullifyEmptyValue(filters.query),
      brand_ids: nullifyEmptyValue(filters.brandIds),
      catalog_ids: nullifyEmptyValue(filters.catalogIds),
      color_ids: nullifyEmptyValue(filters.colorIds),
      material_ids: nullifyEmptyValue(filters.materialIds),
      video_game_rating_ids: nullifyEmptyValue(filters.videoGameRatingIds),
      size_ids: nullifyEmptyValue(filters.sizeIds),
      status_ids: nullifyEmptyValue(filters.statusIds),
      price_from: filters.priceFrom ? parseFloat(filters.priceFrom) : null,
      price_to: filters.priceTo ? parseFloat(filters.priceTo) : null,
      order: nullifyEmptyValue(filters.sortBy),
      popular: filters.isPopularCatalog
    }
  };

  if (searchStartType) {
    extra.search_start_type = searchStartType;
  }

  if (searchStartId) {
    extra.search_start_id = searchStartId;
  }

  selectedDynamicFilters.filter(_ref4 => {
    let {
      ids
    } = _ref4;
    return ids.length;
  }).forEach(_ref5 => {
    let {
      type,
      ids
    } = _ref5;
    if (!extra.selected_filters) extra.selected_filters = {};
    extra.selected_filters[type] = ids; // TO DO: remove this when dynamic catalog filters scaled

    extra.conditions["".concat(type, "_ids")] = ids;
  });
  return {
    event: 'user.filter_items',
    extra
  };
};

const settingsUserInputEvent = args => {
  const {
    screen,
    target,
    isValid,
    targetDetails
  } = args;
  const extra = {
    screen,
    target,
    valid: isValid,
    target_details: targetDetails
  };
  return {
    event: 'settings.user.input',
    extra
  };
};

const userRegisterSuccessEvent = args => {
  const {
    type,
    userId
  } = args;
  const extra = {
    type
  };
  return {
    event: 'user.register_success',
    extra,
    userId
  };
};

const userRegisterFailEvent = args => {
  const {
    type,
    errors
  } = args;
  const extra = {
    type,
    reason: 'validation_error',
    details: errors === null || errors === void 0 ? void 0 : errors.map(error => error.value).join(', ')
  };
  return {
    event: 'user.register_fail',
    extra
  };
};

const authenticateSuccessEvent = args => {
  const {
    type,
    userId
  } = args;
  const extra = {
    type
  };
  return {
    event: 'user.authenticate_success',
    extra,
    userId
  };
};

const authenticateFailEvent = args => {
  const {
    type,
    errors,
    error
  } = args;
  const extra = {
    type,
    reason: 'validation_error',
    details: (errors === null || errors === void 0 ? void 0 : errors.map(_ref6 => {
      let {
        value
      } = _ref6;
      return value;
    }).join(', ')) || error
  };
  return {
    event: 'user.authenticate_fail',
    extra
  };
};

const selectSuggestedMessageEvent = args => {
  const {
    id,
    position
  } = args;
  const extra = {
    id
  };
  if (position) extra.position = position;
  return {
    event: 'user.click_suggested_message',
    extra
  };
};

const viewSuggestedMessageEvent = args => {
  const {
    id,
    position
  } = args;
  const extra = {
    id
  };
  if (position) extra.position = position;
  return {
    event: 'user.view_suggested_message',
    extra
  };
};

const donationsClickEvent = args => {
  const {
    target,
    screen,
    targetDetails
  } = args;
  const extra = {
    target
  };
  if (screen) extra.screen = screen;
  if (targetDetails) extra.target_details = targetDetails;
  return {
    event: 'user.click_donations',
    extra
  };
};

const fundraiserClickEvent = args => {
  const {
    target,
    screen,
    targetDetails
  } = args;
  const extra = {
    target
  };
  if (screen) extra.screen = screen;
  if (targetDetails) extra.target_details = targetDetails;
  return {
    event: 'user.click_donations',
    extra
  };
};

const buyerLoadShippingPointsEvent = args => {
  const {
    transactionId,
    shippingPointsCount,
    screen,
    latitude,
    longitude
  } = args;
  const extra = {
    screen,
    latitude,
    longitude,
    shipping_points_count: shippingPointsCount
  };
  if (transactionId) extra.transaction_id = transactionId;
  return {
    event: 'buyer.load_shipping_points',
    extra
  };
};

const buyerPreselectedShippingOptionsEvent = args => {
  const {
    screen,
    transactionId,
    isNewEntry,
    carrier,
    shippingPoint,
    distance
  } = args;
  const extra = {
    screen,
    carrier,
    shipping_point: shippingPoint
  };
  if (transactionId) extra.transaction_id = transactionId;
  if (distance !== undefined) extra.distance = distance;
  if (isNewEntry) extra.is_new_entry = isNewEntry;
  return {
    event: 'buyer.preselected_shipping_option',
    extra
  };
};

const buyerPickDeliveryTypeEvent = args => {
  const {
    transactionId,
    carrier,
    isNewEntry,
    success,
    deliveryType
  } = args;
  const extra = {
    success,
    carrier,
    delivery_type: deliveryType,
    is_new_entry: isNewEntry
  };
  if (transactionId) extra.transaction_id = transactionId;
  return {
    event: 'buyer.pick_delivery_type',
    extra
  };
};

const buyerWantItemEvent = _ref7 => {
  let {
    transactionId,
    itemId,
    button,
    globalSearchSessionId,
    searchSessionId,
    homepageSessionId
  } = _ref7;
  const extra = {
    button,
    item_id: itemId,
    transaction_id: transactionId,
    global_search_session_id: globalSearchSessionId,
    search_session_id: searchSessionId,
    homepage_session_id: homepageSessionId
  };
  return {
    event: 'buyer.want_item',
    extra
  };
};

const captchaResolveEvent = () => {
  return {
    event: 'user.resolve_captcha'
  };
};

const confirmWalletEvent = args => {
  const extra = {
    source: args.source
  };
  return {
    event: 'user.confirm_wallet',
    extra
  };
};

const saveBankAccountEvent = args => {
  const extra = {
    valid: args.valid,
    failure_reason: args.failure_reason,
    details: args.details
  };
  return {
    event: 'user.save_bank_account',
    extra
  };
};

const itemUploadStartEventExtra = args => {
  const extra = {
    screen: args.screenName,
    upload_session_id: args.tempUuid
  };
  return {
    event: 'user.upload_item.start',
    extra
  };
};

const itemUploadFieldSetEvent = args => {
  const {
    fieldName,
    tempUuid,
    suggestionSeen,
    suggestions,
    userSelection,
    query,
    position,
    querySuggestions
  } = args;
  const extra = {
    suggestions: suggestions.length ? suggestions : null,
    field_name: fieldName,
    upload_session_id: tempUuid,
    suggestion_seen: suggestionSeen,
    user_selection: userSelection !== null && userSelection !== void 0 && userSelection.length ? userSelection : null
  };
  if (query) extra.query = query;
  if (position) extra.position = position;

  if (querySuggestions) {
    extra.query_suggestions = querySuggestions.length ? querySuggestions : null;
  }

  return {
    event: 'user.upload_item.field_set',
    extra
  };
};

const itemUploadSubmitFail = args => {
  const extra = {
    reason: args.reason,
    upload_session_id: args.tempUuid,
    validation_errors: args.validationErrors,
    screen: args.screen
  };
  return {
    event: 'user.upload_item.submit_fail',
    extra
  };
};

const itemUploadSubmitSuccess = args => {
  const extra = {
    edited: args.edited,
    photo_count: args.photoCount,
    video_count: args.videoCount,
    item_id: args.itemId,
    screen: args.screen,
    upload_session_id: args.tempUuid
  };
  return {
    event: 'user.upload_item.submit_success',
    extra
  };
};

const itemUploadFillZipCodeFail = () => ({
  event: 'user.upload_item.fill_zip_code_fail'
});

const toggleSearchSubscriptionEvent = args => {
  const {
    isSubscribing,
    searchTitle,
    searchQuery,
    searchSessionId,
    globalSearchSessionId
  } = args;
  const extra = {
    is_subscribing: isSubscribing,
    global_search_session_id: globalSearchSessionId
  };
  if (searchTitle) extra.search_title = searchTitle;
  if (searchQuery) extra.search_query = searchQuery;
  if (searchSessionId) extra.search_session_id = searchSessionId;
  return {
    event: 'user.toggle_search_subscription',
    extra
  };
};

const selectSavedSearchEvent = args => {
  const {
    type,
    searchTitle,
    searchSessionId,
    globalSearchSessionId,
    newItemsCount,
    unrestrictedNewItemsCount
  } = args;
  const extra = {
    type,
    search_title: searchTitle,
    global_search_session_id: globalSearchSessionId
  };
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (newItemsCount) extra.new_items_count = newItemsCount;
  if (unrestrictedNewItemsCount) extra.unrestricted_new_items_count = unrestrictedNewItemsCount;
  return {
    event: 'user.select_saved_search',
    extra
  };
};

const abTestExposeEvent = args => {
  const {
    id,
    name,
    anonId,
    userId,
    countryCode,
    variant
  } = args;
  const extra = {
    test_id: String(id),
    test_name: name,
    test_anon_id: anonId,
    country_code: countryCode
  };
  if (variant) extra.variant = variant;
  if (userId) extra.test_user_id = String(userId);
  return {
    event: 'ab_test.expose',
    extra
  };
};

const sharingReferCopyLinkEvent = () => ({
  event: 'sharing.refer.copy_link'
});

const buyerViewAddContactDetailsEvent = args => {
  const {
    transactionId,
    screen
  } = args;
  const extra = {
    transaction_id: transactionId,
    screen
  };
  return {
    event: 'buyer.view_add_contact_details',
    extra
  };
};

const sellerViewAddContactDetailsEvent = args => {
  const {
    transactionId,
    screen
  } = args;
  const extra = {
    transaction_id: transactionId,
    screen
  };
  return {
    event: 'seller.view_add_contact_details',
    extra
  };
};

const makeOfferEvent = args => {
  const {
    isBuyer,
    currentPrice,
    offeredPrice,
    transactionId
  } = args;
  const extra = {
    transaction_id: transactionId,
    current_price: currentPrice,
    offered_price: offeredPrice
  };
  return {
    event: isBuyer ? 'buyer.make_offer_request' : 'seller.make_offer',
    extra
  };
};

const sharingReferralsOpenInfoBannerLink = args => {
  const {
    tabName
  } = args;
  const extra = {
    tab_name: tabName
  };
  return {
    event: 'sharing.referrals.open_info_banner_link',
    extra
  };
};

const buyerPrecheckoutEvent = args => {
  const {
    transactionId
  } = args;
  const extra = {
    transaction_id: transactionId
  };
  return {
    event: 'buyer.precheckout',
    extra
  };
};

const userPaginateCatalogEvent = args => {
  const {
    currentPage,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId
  } = args;
  const extra = {
    page: currentPage
  };
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId;
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  return {
    event: 'user.paginate_catalog',
    extra
  };
};

const userToggleBrandFilterSuggestionEvent = args => {
  const {
    brandId,
    query,
    position,
    suggestionsSessionId,
    suggestedBrands,
    selectedBrands,
    globalSearchSessionId,
    screen
  } = args;
  const extra = {
    query,
    position,
    brand_id: brandId,
    suggestions_session_id: suggestionsSessionId,
    suggested_brands: suggestedBrands,
    selected_brands: selectedBrands
  };
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  if (screen) extra.screen = screen;
  return {
    event: 'user.toggle_brand_filter_suggestion',
    extra
  };
};

const userAppliedOrderEvent = args => {
  const {
    order,
    searchSessionId,
    globalSearchSessionId
  } = args;
  const extra = {
    order
  };
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  return {
    event: 'user.applied_order',
    extra
  };
};

const userSelectTheme = _ref8 => {
  let {
    theme
  } = _ref8;
  const extra = {
    theme
  };
  return {
    event: 'user.select_theme',
    extra
  };
};

const conversationHarassmentEvent = args => {
  const {
    conversationId,
    wasEdited,
    correlationId
  } = args;
  const extra = {
    conversation_id: conversationId,
    is_message_edited: wasEdited,
    correlation_id: correlationId
  };
  return {
    event: 'user.react_to_conversation_harassment_warning',
    extra
  };
};

const buyerViewDeliveryDetailsEvent = args => {
  const {
    transactionId,
    screen
  } = args;
  const extra = {
    transaction_id: transactionId,
    screen
  };
  return {
    event: 'buyer.view_delivery_details',
    extra
  };
};

const buyerBuyEvent = args => {
  const {
    transactionId,
    screen,
    globalSearchSessionId,
    homepageSessionId
  } = args;
  const extra = {
    transaction_id: transactionId,
    screen,
    global_search_session_id: globalSearchSessionId,
    homepage_session_id: homepageSessionId
  };
  return {
    event: 'buyer.buy',
    extra
  };
};

const requestedAdEvent = args => {
  const {
    screen,
    adUnitPath
  } = args;
  const extra = {
    placement_id: adUnitPath,
    screen
  };
  return {
    event: 'system.requested_ad',
    extra
  };
};

const receivedAdEvent = args => {
  const {
    adUnitPath,
    isMobileWeb
  } = args;
  const extra = {
    placement_id: adUnitPath,
    is_mobile_web: isMobileWeb
  };
  return {
    event: 'user.received_ad',
    extra
  };
};

const viewAdEvent = args => {
  const {
    adUnitPath,
    shape,
    size,
    isMobileWeb
  } = args;
  const eventSize = Array.isArray(size) ? size.join('x') : String(size);
  const extra = {
    placement_id: adUnitPath,
    shape,
    size: eventSize,
    // e.g. 728x90
    is_mobile_web: isMobileWeb
  };
  return {
    event: 'user.view_ad',
    extra
  };
};

const crashAdComponentEvent = args => {
  const {
    error,
    pageName,
    placementId,
    dfpCode,
    kind
  } = args;
  return {
    event: 'system.ad_component_crash',
    extra: {
      error,
      screen_name: pageName,
      placement_id: placementId,
      kind,
      dfp_code: dfpCode
    }
  };
};

const prebidTimedOutEvent = () => {
  return {
    event: 'system.prebid_timed_out'
  };
};

export let CheckoutInteractionType;

(function (CheckoutInteractionType) {
  CheckoutInteractionType["Scrolled"] = "scrolled";
  CheckoutInteractionType["ReturnedBack"] = "returned_back";
  CheckoutInteractionType["ScreenOnDuration"] = "screen_on_duration";
  CheckoutInteractionType["ScreenLoadDuration"] = "screen_load_duration";
})(CheckoutInteractionType || (CheckoutInteractionType = {}));

export let CheckoutScrollInteractionDetailsType;

(function (CheckoutScrollInteractionDetailsType) {
  CheckoutScrollInteractionDetailsType["NoScrolling"] = "no_scrolling";
  CheckoutScrollInteractionDetailsType["PartialScrolling"] = "partial_scrolling";
  CheckoutScrollInteractionDetailsType["ScrolledTillTheEnd"] = "scrolled_till_the_end";
  CheckoutScrollInteractionDetailsType["NoScrollingSawCompleteScreen"] = "no_scrolling_saw_complete_screen";
})(CheckoutScrollInteractionDetailsType || (CheckoutScrollInteractionDetailsType = {}));

const checkoutInteractionEvent = args => {
  const {
    screen,
    action,
    actionDetails,
    transactionId
  } = args;
  return {
    event: 'buyer.interacts_with_checkout',
    extra: {
      screen,
      action,
      transaction_id: transactionId,
      action_details: actionDetails ? JSON.stringify(actionDetails) : null
    }
  };
};

const favouriteGroupingCreateSuccessEvent = args => {
  const {
    screen,
    target
  } = args;
  return {
    event: 'user.favourite_grouping.create_success',
    extra: {
      screen,
      target
    }
  };
};

const favouriteGroupingCreateFailEvent = args => {
  const {
    screen,
    target,
    target_details
  } = args;
  return {
    event: 'user.favourite_grouping.create_fail',
    extra: {
      screen,
      target,
      target_details
    }
  };
};

const favouriteGroupingRenameSuccessEvent = args => {
  const {
    screen,
    target
  } = args;
  return {
    event: 'user.favourite_grouping.rename_success',
    extra: {
      screen,
      target
    }
  };
};

const favouriteGroupingRenameFailEvent = args => {
  const {
    screen,
    target,
    target_details
  } = args;
  return {
    event: 'user.favourite_grouping.rename_fail',
    extra: {
      screen,
      target,
      target_details
    }
  };
};

const viewSuggestedPlacesEvent = args => {
  const {
    query,
    count,
    sessionToken,
    screen
  } = args;
  return {
    event: 'user.autocomplete_results',
    extra: {
      query,
      count,
      session_token: sessionToken,
      screen
    }
  };
};

const selectSuggestedPlaceEvent = args => {
  const {
    selectedAutocomplete,
    sessionToken,
    screen
  } = args;
  return {
    event: 'user.autocomplete_details',
    extra: {
      selected_autocomplete: selectedAutocomplete,
      session_token: sessionToken,
      screen
    }
  };
};

const filterFeaturedCollectionItemsEvent = args => {
  const {
    sortBy,
    catalogId,
    screen
  } = args;
  const extra = {
    sort_by: sortBy,
    catalog_id: catalogId || null
  };
  if (screen) extra.screen = screen;
  return {
    event: 'user.filter_featured_collections',
    extra
  };
};

const sizeFilterEvent = args => {
  const {
    sizeIds
  } = args;
  let eventName = 'user.disable_size_filter';
  const extra = {};

  if (sizeIds.length > 0) {
    eventName = 'user.enable_size_filter';
    extra.size_ids = sizeIds;
  }

  return {
    event: eventName,
    extra
  };
};

const userViewItemGallery = args => {
  const {
    imagePosition,
    imageCount,
    itemId,
    isLoop,
    contentType,
    galleryContainsAd
  } = args;
  const extra = {
    // This value is hardcoded, because we have gallery only in a modal form
    // hence the `fullscreen` name. In apps, they have two modes to show gallery,
    // in overlay form or in the page itself, hence they have different values here.
    image_view: 'fullscreen',
    image_position: imagePosition,
    number_item_images: imageCount,
    item_id: itemId,
    is_loop: isLoop,
    content_type: contentType,
    gallery_contains_ad: galleryContainsAd
  };
  return {
    event: 'user.view_item_gallery',
    extra
  };
};

const userViewDynamicFilterOptionEvent = args => {
  const {
    id,
    type,
    position,
    itemsCount,
    searchText,
    filteringSessionId,
    searchSessionId,
    globalSearchSessionId,
    fromScreen
  } = args;
  const extra = {
    id,
    position,
    type,
    code: type,
    items_count: itemsCount,
    search_text: searchText,
    filtering_session_id: filteringSessionId,
    from_screen: fromScreen
  };
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  return {
    event: 'user.view_filter_option',
    extra
  };
};

const userClickDynamicFilterOptionEvent = args => {
  const {
    id,
    type,
    position,
    isSelected,
    itemsCount,
    searchText,
    filteringSessionId,
    searchSessionId,
    globalSearchSessionId,
    fromScreen
  } = args;
  const extra = {
    id,
    position,
    type,
    code: type,
    is_selected: isSelected,
    items_count: itemsCount,
    search_text: searchText,
    filtering_session_id: filteringSessionId,
    from_screen: fromScreen
  };
  if (searchSessionId) extra.search_session_id = searchSessionId;
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId;
  return {
    event: 'user.click_filter_option',
    extra
  };
};

const searchFaqEntriesEvent = args => {
  const extra = {
    search_session_id: args.searchSessionId,
    search_query: args.searchQuery,
    faq_entry_ids: args.faqEntryIds
  };
  return {
    event: 'user.search_faq_entries',
    extra
  };
};

const leaveFaqEntryEvent = args => {
  const extra = {
    faq_entry_id: args.faqEntryId,
    faq_access_channel: args.faqAccessChannel,
    initial_page_scroll_depth: args.initialPageScrollDepth,
    max_page_scroll_depth: args.maxPageScrollDepth,
    total_view_time: args.totalViewTime
  };
  return {
    event: 'user.leave_faq_entry',
    extra
  };
};

const viewFaqEntry = args => {
  const extra = {
    faq_entry_id: args.faqEntryId,
    faq_entry_channel: args.faqEntryChannel,
    faq_access_channel: args.faqAccessChannel,
    screen: args.screen
  };
  return {
    event: 'user.view_faq_entry',
    extra
  };
};

const adBlockCheckEvent = args => {
  const {
    isMobileWeb,
    isAdBlockerUsed
  } = args;
  const extra = {
    is_mobile_web: isMobileWeb,
    is_adblock_used: isAdBlockerUsed
  };
  return {
    event: 'user.adblock_check',
    extra
  };
};

const timingEvent = args => {
  const {
    section,
    duration,
    completionState,
    data
  } = args;
  const extra = {
    section,
    duration,
    completion_state: completionState,
    data: data || null
  };
  return {
    event: 'system.timing',
    extra
  };
};

const buyerViewCarrierDiscountedShippingPriceEvent = args => {
  const {
    screen,
    transactionId
  } = args;
  const extra = {
    screen,
    transaction_id: transactionId
  };
  return {
    event: 'buyer.view_carrier_discounted_shipping_price',
    extra
  };
};

const conversationPasteEvent = conversationId => ({
  event: 'user.conversation_paste',
  extra: {
    conversation_id: conversationId
  }
});

const userModifyCarrierPreferencesEvent = args => {
  const {
    screen,
    target,
    targetDetails
  } = args;
  const extra = {
    screen,
    target,
    target_details: targetDetails
  };
  return {
    event: 'user.modify_carrier_preferences',
    extra
  };
};

const userViewHomepageBlock = args => {
  const {
    blockName,
    blockPosition,
    homepageSessionId,
    id
  } = args;
  const extra = {
    block_name: blockName,
    block_position: blockPosition
  };
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId;
  if (id) extra.id = id;
  return {
    event: 'user.view_homepage_block',
    extra
  };
};

const userViewHomepageBlockCta = args => {
  const {
    type,
    blockName,
    blockPosition,
    homepageSessionId,
    id
  } = args;
  const extra = {
    type,
    block_name: blockName,
    block_position: blockPosition
  };
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId;
  if (id) extra.id = id;
  return {
    event: 'user.view_homepage_block_cta',
    extra
  };
};

const userClickHomepageBlockCta = args => {
  const {
    type,
    blockName,
    blockPosition,
    homepageSessionId,
    id
  } = args;
  const extra = {
    type,
    block_name: blockName,
    block_position: blockPosition
  };
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId;
  if (id) extra.id = id;
  return {
    event: 'user.click_homepage_block_cta',
    extra
  };
};

const sellerActionOfferRequest = args => {
  const {
    action,
    transactionId,
    currentPrice,
    offeredPrice
  } = args;
  const extra = {
    transaction_id: transactionId,
    current_price: Number(currentPrice),
    offered_price: Number(offeredPrice)
  };
  return {
    event: "seller.".concat(action, "_offer_request"),
    extra
  };
};

const userEditPhotoEvent = args => {
  const extra = {
    upload_session_id: args.tempUuid,
    extra_photo_id: args.photoId
  };
  return {
    event: 'user.edit_photo',
    extra
  };
};

const checkoutBuyerTimeOnTaskEvent = args => {
  const extra = {
    transaction_id: args.transactionId,
    screen: args.screen,
    duration: args.duration,
    timestamp: JSON.stringify({
      utc_time_from: args.timestamp.utcTimeFrom,
      utc_time_to: args.timestamp.utcTimeTo
    })
  };
  return {
    event: 'buyer.time_in_checkout',
    extra
  };
};

const userClickHomepageElement = args => {
  const extra = {
    block_name: args.blockName,
    position: args.position,
    content_source: args.contentSource,
    content_source_label: args.contentSourceLabel,
    homepage_session_id: args.homepageSessionId,
    screen: args.screen
  };
  if (args.contentSourceId) extra.content_source_id = args.contentSourceId;
  if (args.extraAttributes) extra.extra_attributes = args.extraAttributes;
  return {
    event: 'user.click_homepage_element',
    extra
  };
};

const userViewHomepageElement = args => {
  const extra = {
    block_name: args.blockName,
    position: args.position,
    content_source: args.contentSource,
    content_source_label: args.contentSourceLabel,
    homepage_session_id: args.homepageSessionId,
    screen: args.screen
  };
  if (args.contentSourceId) extra.content_source_id = args.contentSourceId;
  if (args.extraAttributes) extra.extra_attributes = args.extraAttributes;
  return {
    event: 'user.view_homepage_element',
    extra
  };
};

const noticesUserOpenFaq = args => {
  const extra = {
    thread_id: args.threadId
  };
  return {
    event: 'notices.user.open_faq',
    extra
  };
};

const noticesUserOpenContactForm = args => {
  const extra = {
    thread_id: args.threadId
  };
  return {
    event: 'notices.user.open_contact_form',
    extra
  };
};

const noticesUserSubmitTicket = args => {
  const extra = {
    thread_id: args.threadId
  };
  return {
    event: 'notices.user.submit_ticket',
    extra
  };
};

const userClickMemberEvent = args => {
  const extra = {
    user_id: args.userId,
    content_source: args.contentSource
  };
  return {
    event: 'user.click_member',
    extra
  };
};

const tokenizationOutcomeEvent = args => {
  const {
    outcome,
    orderId,
    checkoutType,
    errorMessage,
    providerName,
    transactionId
  } = args;
  const extra = {
    outcome,
    order_id: orderId,
    error_message: errorMessage,
    provider_name: providerName,
    transaction_id: transactionId,
    checkout_type: extraServiceOrderTypeToTrackingTypeMap[checkoutType] || checkoutType
  };
  return {
    event: 'system.tokenization_outcome',
    extra
  };
};

const dsaUserOpenAppealForm = args => {
  const extra = {
    thread_id: args.threadId.toString()
  };
  return {
    event: 'dsa.user.open_appeal_form',
    extra
  };
};

const dsaUserOpenAppealEducation = args => {
  const extra = {
    thread_id: args.threadId.toString()
  };
  return {
    event: 'dsa.user.open_appeal_education',
    extra
  };
};

const dsaUserOpenAppealAgreement = args => {
  const extra = {
    thread_id: args.threadId.toString()
  };
  return {
    event: 'dsa.user.open_appeal_agreement',
    extra
  };
};

const dsaUserAddPhotosAppealForm = args => {
  const extra = {
    thread_id: args.threadId.toString()
  };
  return {
    event: 'dsa.user.add_photos_appeal_form',
    extra
  };
};

const dsaUserSubmitAppealForm = args => {
  const extra = {
    thread_id: args.threadId.toString()
  };
  return {
    event: 'dsa.user.submit_appeal_form',
    extra
  };
};

const dsaNtdUserOpenAppealForm = args => {
  const extra = {
    reference_id: args.referenceId
  };
  return {
    event: 'dsa_ntd.user.open_appeal_form',
    extra
  };
};

const dsaNtdUserSubmitAppealForm = args => {
  const extra = {
    reference_id: args.referenceId
  };
  return {
    event: 'dsa_ntd.user.submit_appeal_form',
    extra
  };
};

const taxRulesPlacementClickEvent = args => {
  const {
    targetDetails,
    screen
  } = args;
  const extra = {
    target_details: targetDetails,
    screen
  };
  return {
    event: 'user.click',
    extra
  };
};

const ViewLandingPageEvent = args => {
  const {
    screen,
    path,
    referrer
  } = args;
  const extra = {
    screen,
    path,
    referrer
  };
  return {
    event: 'user.view_screen',
    extra
  };
};

const referralsOpenLinkEvent = args => {
  const {
    referrerId
  } = args;
  const extra = {
    referrer_id: referrerId
  };
  return {
    event: 'sharing.referrals.open_link',
    extra
  };
};

const firstTimeListerEducationOpenEvent = () => {
  return {
    event: 'user.first_time_lister_education_open'
  };
};

const firstTimeListerEducationCloseEvent = args => {
  const {
    slideNumber
  } = args;
  return {
    event: 'user.first_time_lister_education_close',
    extra: {
      slide_number: slideNumber
    }
  };
};

const viewItemEvent = args => {
  const {
    itemId,
    ownerId,
    referrerPath,
    searchSessionId
  } = args;
  return {
    event: 'user.view_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId
    }
  };
};

const viewOwnItemEvent = args => {
  const {
    itemId,
    ownerId,
    referrerPath,
    searchSessionId
  } = args;
  return {
    event: 'user.view_current_user_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId
    }
  };
};

export { clickEvent, kycClickEvent, favouriteItemEvent, followUserEvent, followBrandEvent, impressionEvent, clickListItemEvent, clickClosetPromotionEvent, endOfListEvent, viewEvent, viewScreenEvent, viewUserEvent, kycViewScreenEvent, systemTraceClosetPromoEvent, conversationReplyEvent, selectSearchSuggestionEvent, viewSearchSuggestionEvent, viewSearchSuggestionsEvent, viewShippingPriceEvent, clickFilterEvent, startConversationEvent, checkoutBuyerInputEvent, transactionSellerInputEvent, userInputEvent, checkoutPickPaymentMethodEvent, buyerViewCheckoutEvent, buyerViewSettingsCheckoutEvent, userViewCheckoutEvent, selectCatalogEvent, selfServiceViewScreenEvent, viewPopularSearchEvent, selectPopularSearchEvent, selectBrandEvent, userFilterItemsEvent, settingsUserInputEvent, userRegisterSuccessEvent, userRegisterFailEvent, userEditPhotoEvent, authenticateSuccessEvent, authenticateFailEvent, selectSuggestedMessageEvent, viewSuggestedMessageEvent, donationsClickEvent, fundraiserClickEvent, buyerLoadShippingPointsEvent, buyerPreselectedShippingOptionsEvent, buyerPickDeliveryTypeEvent, captchaResolveEvent, confirmWalletEvent, itemUploadStartEventExtra, itemUploadFieldSetEvent, itemUploadSubmitFail, itemUploadSubmitSuccess, itemUploadFillZipCodeFail, saveBankAccountEvent, toggleSearchSubscriptionEvent, selectSavedSearchEvent, abTestExposeEvent, sharingReferCopyLinkEvent, buyerViewAddContactDetailsEvent, sellerViewAddContactDetailsEvent, makeOfferEvent, sharingReferralsOpenInfoBannerLink, userPaginateCatalogEvent, userToggleBrandFilterSuggestionEvent, userAppliedOrderEvent, userSelectTheme, conversationHarassmentEvent, buyerViewDeliveryDetailsEvent, buyerBuyEvent, requestedAdEvent, receivedAdEvent, viewAdEvent, crashAdComponentEvent, prebidTimedOutEvent, checkoutInteractionEvent, favouriteGroupingCreateSuccessEvent, favouriteGroupingCreateFailEvent, favouriteGroupingRenameSuccessEvent, favouriteGroupingRenameFailEvent, viewSuggestedPlacesEvent, selectSuggestedPlaceEvent, viewFilterEvent, buyerPrecheckoutEvent, filterFeaturedCollectionItemsEvent, sizeFilterEvent, userViewItemGallery, userViewDynamicFilterOptionEvent, userClickDynamicFilterOptionEvent, searchFaqEntriesEvent, leaveFaqEntryEvent, viewFaqEntry, adBlockCheckEvent, timingEvent, buyerViewCarrierDiscountedShippingPriceEvent, conversationPasteEvent, userModifyCarrierPreferencesEvent, userViewHomepageBlock, userViewHomepageBlockCta, userClickHomepageBlockCta, sellerActionOfferRequest, buyerWantItemEvent, checkoutBuyerTimeOnTaskEvent, userClickHomepageElement, userViewHomepageElement, noticesUserOpenFaq, noticesUserOpenContactForm, noticesUserSubmitTicket, userClickMemberEvent, tokenizationOutcomeEvent, dsaUserOpenAppealForm, dsaUserOpenAppealEducation, dsaUserOpenAppealAgreement, dsaUserAddPhotosAppealForm, dsaUserSubmitAppealForm, dsaNtdUserOpenAppealForm, dsaNtdUserSubmitAppealForm, taxRulesPlacementClickEvent, ViewLandingPageEvent, referralsOpenLinkEvent, firstTimeListerEducationOpenEvent, firstTimeListerEducationCloseEvent, viewItemEvent, viewOwnItemEvent };